import { Button, Icon, SvgIcon } from '@civilgrid/ui/components';
import type { MultiPolygon } from 'geojson';
import type mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { homeViewStore, setMapGeometryFilter } from '~/pages/homeViewV2/store';

const BOUNDS_TOLERANCE = 1e-6;

/**
 * Converts map bounds to a GeoJSON MultiPolygon (axis-aligned bbox).
 */
function boundsToMultiPolygon(sw: { lng: number; lat: number }, ne: { lng: number; lat: number }): MultiPolygon {
  const ring: [number, number][] = [
    [sw.lng, sw.lat],
    [ne.lng, sw.lat],
    [ne.lng, ne.lat],
    [sw.lng, ne.lat],
    [sw.lng, sw.lat],
  ];
  return {
    type: 'MultiPolygon',
    coordinates: [[ring]],
  };
}

/** Returns true if current map bounds match the stored filter geometry (map hasn't moved). */
function boundsMatchFilter(map: mapboxgl.Map, filter: MultiPolygon): boolean {
  const ring = filter.coordinates?.[0]?.[0];
  if (!ring || ring.length < 3) return false;
  const [[swLng, swLat], , [neLng, neLat]] = ring;
  const bounds = map.getBounds();
  if (!bounds) return false;
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  return (
    Math.abs(sw.lng - swLng) < BOUNDS_TOLERANCE &&
    Math.abs(sw.lat - swLat) < BOUNDS_TOLERANCE &&
    Math.abs(ne.lng - neLng) < BOUNDS_TOLERANCE &&
    Math.abs(ne.lat - neLat) < BOUNDS_TOLERANCE
  );
}

export function FilterTableToMap() {
  const maps = useMap();
  const { mapGeometryFilter } = useSnapshot(homeViewStore);
  const [mapVersion, setMapVersion] = useState(0);

  const homeMap =
    maps && typeof maps === 'object' && 'homeMap' in maps
      ? (maps as { homeMap?: { current?: mapboxgl.Map } }).homeMap
      : undefined;
  const map =
    homeMap?.current ??
    (maps && typeof maps === 'object' && 'current' in maps ? (maps as { current?: mapboxgl.Map }).current : undefined);

  useEffect(() => {
    if (!map) return;
    const onMoveEnd = () => setMapVersion((v) => v + 1);
    map.on('moveend', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
    };
  }, [map]);

  return (
    <FilterTableToMapInner
      mapRef={
        homeMap ??
        (maps && typeof maps === 'object' && 'current' in maps ? (maps as { current?: mapboxgl.Map }) : undefined)
      }
      map={map}
      mapGeometryFilter={mapGeometryFilter ? (mapGeometryFilter as MultiPolygon) : null}
      mapVersion={mapVersion}
    />
  );
}

function FilterTableToMapInner({
  mapRef,
  map,
  mapGeometryFilter,
  mapVersion,
}: {
  mapRef: { current?: mapboxgl.Map } | undefined;
  map: mapboxgl.Map | undefined;
  mapGeometryFilter: MultiPolygon | null;
  mapVersion: number;
}) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const isClearFilter = useMemo(() => {
    return !!map && !!mapGeometryFilter && boundsMatchFilter(map, mapGeometryFilter);
  }, [map, mapGeometryFilter, mapVersion]);

  const handleClick = useCallback(() => {
    const mapInstance = mapRef?.current ?? map;
    if (!mapInstance) return;
    if (isClearFilter) {
      setMapGeometryFilter(null);
    } else {
      const bounds = mapInstance.getBounds();
      if (!bounds) return;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const geometry = boundsToMultiPolygon({ lng: sw.lng, lat: sw.lat }, { lng: ne.lng, lat: ne.lat });
      setMapGeometryFilter(geometry);
    }
  }, [mapRef, map, isClearFilter]);

  if (isClearFilter) {
    return (
      <Button variant="secondary" size="standard" onClick={handleClick} className="py-1.25">
        <Icon icon="crossCircled" size={14} /> Clear filter
      </Button>
    );
  }

  return (
    <Button variant="discovery" size="standard" onClick={handleClick} className="py-1.25">
      <SvgIcon icon="filter" size={14} /> Filter Table to Map View
    </Button>
  );
}
